import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import selectWebsocket from '../Websocket/selectors';
import { makeSelectArchiveMode } from '../App/selectors';
import { cltStatusReceived } from '../Websocket/actions';
import useLockedCltStatus from './hooks/useLockedCltStatus';
import { setIn, getIn, parseInput } from './utils';
import PageView from './PageView';
import HudView from './HudView';

const LineStatusViewerBase = ({ websocket, archiveMode, dispatch, variant }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [supportsTouch, setSupportsTouch] = useState(false);
  const { data, setData, reload, controlVariables } = useLockedCltStatus(websocket, variant === 'hud', variant === 'hud' && archiveMode);
  const [fileLoadedJson, setFileLoadedJson] = useState(null);
  const [pinnedNodes, setPinnedNodes] = useState([]);
  const [activeInlineAddPath, setActiveInlineAddPath] = useState(null);
  const [priorityMap, setPriorityMap] = useState({});
  const [exactMatchOnly, setExactMatchOnly] = useState(false);
  const [recentDiffs, setRecentDiffs] = useState([]);
  const [loadingRecentDiffs, setLoadingRecentDiffs] = useState(variant === 'hud');

  const loadRecentDiffs = useCallback(() => {
    setLoadingRecentDiffs(true);
    axios
      .get('/api/v1/productionline/linestatus/latest_diffs/')
      .then(({ data: latestDiffs }) => setRecentDiffs(latestDiffs))
      .catch(() => setRecentDiffs([]))
      .finally(() => setLoadingRecentDiffs(false));
  }, []);

  useEffect(() => {
    if (variant !== 'hud') return undefined;
    loadRecentDiffs();
    return undefined;
  }, [loadRecentDiffs, variant]);

  const reloadLiveStatus = useCallback(() => {
    reload();
    if (variant === 'hud') loadRecentDiffs();
  }, [loadRecentDiffs, reload, variant]);

  const treeData = fileLoadedJson !== null ? fileLoadedJson : data;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(hover: none) and (pointer: coarse)');
    const updateSupport = () => setSupportsTouch(mediaQuery.matches);
    updateSupport();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateSupport);
      return () => mediaQuery.removeEventListener('change', updateSupport);
    }
    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(updateSupport);
      return () => mediaQuery.removeListener(updateSupport);
    }
  }, []);

  const commitMapUpdate = useCallback(
    (updatedMap) => {
      const currentCltstatus = websocket?.websocket?.cltstatus;
      if (currentCltstatus && typeof currentCltstatus === 'object') {
        dispatch(cltStatusReceived({ ...currentCltstatus, map: updatedMap }));
      } else {
        dispatch(cltStatusReceived({ map: updatedMap }));
      }
    },
    [dispatch, websocket]
  );

  const handleEdit = useCallback(
    (keyPath, newValue) => {
      if (fileLoadedJson !== null) {
        setFileLoadedJson((prev) => {
          if (prev === undefined) return prev;
          const next = setIn(prev, keyPath, newValue);
          commitMapUpdate(next);
          return next;
        });
        return;
      }
      setData((prev) => {
        if (prev === undefined) return prev;
        const next = setIn(prev, keyPath, newValue);
        commitMapUpdate(next);
        return next;
      });
    },
    [fileLoadedJson, setData, commitMapUpdate]
  );

  const handleSearchChange = useCallback((event) => setSearchTerm(event.target.value), []);

  const handleExactMatchToggle = useCallback((next) => {
    setExactMatchOnly(Boolean(next));
  }, []);

  const prioritizeKey = useCallback((parentPathKey, childKey) => {
    if (!childKey) return;
    const normalizedParent = parentPathKey || '';
    setPriorityMap((prev) => {
      const existing = prev[normalizedParent] || [];
      if (existing.includes(childKey)) return prev;
      return { ...prev, [normalizedParent]: [childKey, ...existing] };
    });
  }, []);

  const handleInlineAddNode = useCallback(
    ({ name, value }) => {
      if (!name) return false;
      const trimmed = name.trim();
      if (!trimmed) return false;
      if (fileLoadedJson !== null) {
        const base = typeof fileLoadedJson === 'object' && !Array.isArray(fileLoadedJson) ? fileLoadedJson : {};
        if (Object.prototype.hasOwnProperty.call(base, trimmed)) return false;
        const next = { [trimmed]: value, ...base };
        setFileLoadedJson(next);
        commitMapUpdate(next);
        prioritizeKey('', trimmed);
        return true;
      }
      let inserted = false;
      setData((prev) => {
        const base = prev && typeof prev === 'object' && !Array.isArray(prev) ? prev : {};
        if (Object.prototype.hasOwnProperty.call(base, trimmed)) return prev;
        inserted = true;
        const next = { [trimmed]: value, ...base };
        commitMapUpdate(next);
        return next;
      });
      if (inserted) {
        prioritizeKey('', trimmed);
      }
      return inserted;
    },
    [fileLoadedJson, setData, commitMapUpdate, prioritizeKey]
  );

  const handleAddProperty = useCallback(
    ({ keyPath, propertyName, propertyValue, propertyMode = 'primitive', targetType = 'object', arrayIndex }) => {
      if (!Array.isArray(keyPath)) return;
      const parentPathKey = keyPath.length ? keyPath.join('.') : '';
      const parseNewValue = (originalSample) => {
        if (propertyMode === 'object') return {};
        if (propertyMode === 'array') return [];
        return parseInput(originalSample, propertyValue);
      };

      if (fileLoadedJson !== null) {
        const prev = fileLoadedJson;
        if (prev === undefined) return;
        const currentParent = getIn(prev, keyPath);
        if (!currentParent || typeof currentParent !== 'object') return;

        if (Array.isArray(currentParent)) {
          if (targetType !== 'array') return;
          const nextArray = currentParent.slice();
          const insertionIndex = typeof arrayIndex === 'number' && arrayIndex >= 0 && Number.isInteger(arrayIndex) ? Math.min(arrayIndex, nextArray.length) : nextArray.length;
          const parsed = parseNewValue(undefined);
          nextArray.splice(insertionIndex, 0, parsed);
          const updated = setIn(prev, keyPath, nextArray);
          setFileLoadedJson(updated);
          commitMapUpdate(updated);
          prioritizeKey(parentPathKey, undefined);
          return;
        }

        if (targetType !== 'object') return;
        const trimmedName = propertyName?.trim();
        if (!trimmedName) return;
        const existingValue = currentParent[trimmedName];
        const parsed = parseNewValue(existingValue);
        const updated = setIn(prev, [...keyPath, trimmedName], parsed);
        setFileLoadedJson(updated);
        commitMapUpdate(updated);
        prioritizeKey(parentPathKey, trimmedName);
        return;
      }

      let addedChildKey = null;
      setData((prev) => {
        if (prev === undefined) return prev;
        const currentParent = getIn(prev, keyPath);
        if (!currentParent || typeof currentParent !== 'object') return prev;

        if (Array.isArray(currentParent)) {
          if (targetType !== 'array') return prev;
          const nextArray = currentParent.slice();
          const insertionIndex = typeof arrayIndex === 'number' && arrayIndex >= 0 && Number.isInteger(arrayIndex) ? Math.min(arrayIndex, nextArray.length) : nextArray.length;
          const parsed = parseNewValue(undefined);
          nextArray.splice(insertionIndex, 0, parsed);
          const updated = setIn(prev, keyPath, nextArray);
          commitMapUpdate(updated);
          return updated;
        }

        if (targetType !== 'object') return prev;
        const trimmedName = propertyName?.trim();
        if (!trimmedName) return prev;
        const existingValue = currentParent[trimmedName];
        const parsed = parseNewValue(existingValue);
        const updated = setIn(prev, [...keyPath, trimmedName], parsed);
        commitMapUpdate(updated);
        addedChildKey = trimmedName;
        return updated;
      });
      if (addedChildKey) {
        prioritizeKey(parentPathKey, addedChildKey);
      }
    },
    [fileLoadedJson, setData, commitMapUpdate, prioritizeKey]
  );

  const handleTogglePin = useCallback(({ keyPath, label, pathKey }) => {
    setPinnedNodes((prev) => {
      const exists = prev.some((item) => item.pathKey === pathKey);
      if (exists) {
        return prev.filter((item) => item.pathKey !== pathKey);
      }
      return [...prev, { keyPath, label, pathKey }];
    });
  }, []);

  const handleUnpin = useCallback((pathKey) => {
    setPinnedNodes((prev) => prev.filter((item) => item.pathKey !== pathKey));
  }, []);

  const pinnedPathSet = useMemo(() => new Set(pinnedNodes.map((item) => item.pathKey)), [pinnedNodes]);

  const resolvedPinned = useMemo(
    () =>
      pinnedNodes.map((item) => ({
        ...item,
        value: getIn(treeData, item.keyPath)
      })),
    [pinnedNodes, treeData]
  );

  const handleInlineAddPathChange = useCallback((pathKey) => setActiveInlineAddPath(pathKey), []);

  const commonProps = {
    searchTerm,
    onSearchChange: handleSearchChange,
    exactMatchOnly,
    onExactMatchToggle: handleExactMatchToggle,
    onReload: reloadLiveStatus,
    treeData,
    onEdit: handleEdit,
    supportsTouch,
    pinnedEntries: resolvedPinned,
    onUnpin: handleUnpin,
    onTogglePin: handleTogglePin,
    pinnedPathSet,
    onAddProperty: handleAddProperty,
    onAddNode: handleInlineAddNode,
    activeInlineAddPath,
    onInlineAddPathChange: handleInlineAddPathChange,
    priorityMap,
    onFileLoaded: (payload) => {
      setFileLoadedJson(payload);
      commitMapUpdate(payload);
    },
    onClearFileLoaded: () => {
      setFileLoadedJson(null);
      reloadLiveStatus();
    },
    hasFileLoaded: fileLoadedJson !== null
  };

  if (variant === 'hud') {
    return <HudView {...commonProps} recentDiffs={recentDiffs} loadingRecentDiffs={loadingRecentDiffs} controlVariables={fileLoadedJson === null ? controlVariables : {}} title="Line Status Debug" />;
  }

  return <PageView {...commonProps} title="CLT Status" />;
};

LineStatusViewerBase.propTypes = {
  websocket: PropTypes.shape({
    websocket: PropTypes.shape({
      cltstatus: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    })
  }),
  archiveMode: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['page', 'hud']).isRequired
};

const mapStateToProps = createStructuredSelector({
  websocket: selectWebsocket(),
  archiveMode: makeSelectArchiveMode()
});

const withConnect = connect(mapStateToProps);
const ConnectedLineStatusViewer = compose(withConnect, memo)(LineStatusViewerBase);

const LineStatusViewerPage = (props) => <ConnectedLineStatusViewer {...props} variant="page" />;
const LineStatusViewerHudComponent = (props) => <ConnectedLineStatusViewer {...props} variant="hud" />;

LineStatusViewerPage.displayName = 'LineStatusViewerPage';
LineStatusViewerHudComponent.displayName = 'LineStatusViewerHud';

LineStatusViewerPage.propTypes = {
  websocket: PropTypes.object,
  dispatch: PropTypes.func
};

LineStatusViewerHudComponent.propTypes = {
  websocket: PropTypes.object,
  dispatch: PropTypes.func
};

export const LineStatusViewerHud = LineStatusViewerHudComponent;
export default LineStatusViewerPage;
